import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { DialogsModule } from "@progress/kendo-angular-dialog";
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  faTriangleExclamation,
  faPlusCircle, faAddressCard,
  faEdit, faTrash, faBan, faDownload
} from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { WindowModule } from "@progress/kendo-angular-dialog";
import { NotificationService, NotificationModule } from "@progress/kendo-angular-notification";
import { LoggerService } from '../../shared/logger/logger.service';
import { AuthTokenService } from '../../auth/auth-token.service';
import { AppEnities, FormModes } from '../../common/constants.lib';
import { MeService } from '../../auth/me.service';
import { CoBenefit, CommonBenefitType, CommonClassification, CommonGoal, CommonIndustry, CommonState, ControlHost, Resource } from '../../models/models.lib';
import { CompositeFilterDescriptor, SortDescriptor, filterBy, distinct, orderBy } from "@progress/kendo-data-query";
import { ExcelModule, GridModule, PageChangeEvent, PagerModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { findNearestNumber, setTitle } from '../../common/utils.lib';
import { ResourceService } from '../resource.service';
import { EditResourceComponent } from '../edit-resource/edit-resource.component';
import {OpportunitiesComponent} from "../opportunities/opportunities.component";
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';

const { RESOURCE } = AppEnities;

@Component({
  selector: 'casi-resource-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink, RouterLinkActive,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    ExcelModule,
    EditResourceComponent,
    OpportunitiesComponent,
    SafeHtmlPipe
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './resource-list.component.html',
  styleUrl: './resource-list.component.scss'
})
export class ResourceListComponent {

  public view: Observable<Resource[]> = new Observable<Resource[]>();
  public rawData: Resource[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Resource[] = filterBy(this.rawData, this.filter);

  public currentItem: Resource = new Resource();
  public currentItemId: number = 0;
  public currentItemTitle: string = "";

  public formMode: FormModes = FormModes.NOTSET;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public canCreate: boolean = false;
  public canUpdate: boolean = false;
  public canDestroy: boolean = false;

  // @ViewChild("appendTo", { read: ViewContainerRef, static: false }) appendTo: ViewContainerRef;
  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  public windowTop = 100;
  public windowLeft = 50;

  public editWinOpen: boolean = false;
  public confirmDeleteItem: boolean = false;
  public viewOpportunitiesOpen: boolean = false;

  public _hostInfo: ControlHost = new ControlHost();

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _subs: Subscription[] = [];
  private _industries: CommonIndustry[] = [];
  private _goals: CommonGoal[] = [];

  constructor(private logger: LoggerService,
    public authTokenService: AuthTokenService,
    public faIcons: FaIconLibrary,
    public dataService: ResourceService,
    private notificationService: NotificationService,
    private me: MeService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    faIcons.addIcons(
      faTriangleExclamation,
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan, faDownload
    );
  }

  public sort: SortDescriptor[] = [
    { field: "title", dir: "asc" }
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_destroy, can_create, can_update } = this.me.allICanDo(RESOURCE)
    this.logger.debug('ngOnInit:permissions: ', can_create, can_update, can_destroy);
    this.canCreate = can_create;
    this.canUpdate = can_update;
    this.canDestroy = can_destroy;

    this._subs.push(this.route.data.subscribe(({
      hostInfo, industryList,
      goalList
    }) => {
      this.logger.debug('ngOnInit:resolves: ', hostInfo);
      this._hostInfo = hostInfo;
      this._industries = industryList;
      this._goals = goalList;
      this.logger.debug('GOALS', this._goals);
      this.logger.debug('IND', this._industries);
    }));
    this.dataService.query();
    this._subs.push(this.dataService.subscribe(items => {
      this.logger.debug('ngOnInit:items: ', items);
      this.rawData = items;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Window handlers


  public onAddClicked(): void {
    this.logger.debug("onAddClicked...");
    this.currentItem = new Resource(this._goals, this._industries);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAddClicked:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'RESOURCE');
    this.editWinOpen = true;
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Resource.createWithLists(dataItem, this._goals, this._industries);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'RESOURCE');
    this.editWinOpen = true;
  }

  public onItemSave(args: any): void {
    this.logger.debug('onItemSave...', args);
    this.handleSaveAndClose(args);
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = dataItem;
    this.confirmDeleteItem = true;
  }

  onConfirmedClose(status: string): void {
    this.logger.debug("onConfirmedClose: ", status, this.currentItem);
    this.confirmDeleteItem = false;
    if (status === 'delete') {
      this.handleRemove(this.currentItem);
    }
  }

  public close(): void {
    this.logger.debug('close...');
    this.editWinOpen = false;
  }


  //#endregion

  //#region Grid handlers

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): string[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => `${item[fieldName]}`);
    // return distinct(this.gridData, fieldName).map((item: any) => `${item[fieldName]}\u200B`);
  }


  //#endregion

  //#region Opportunities

  viewOpportunities(dataItem: any): void {
    this.logger.debug('viewOpportunities:', dataItem);
    this.currentItemId = dataItem.id;
    this.currentItemTitle = dataItem.title;
    this.viewOpportunitiesOpen = true;
  }

  public closeViewOpportunities(): void {
    this.logger.debug('closeViewOpportunities...');
    this.viewOpportunitiesOpen = false;
    this.dataService.query();
  }

  public closeOpportunities(): void {
    this.logger.debug('closeOpportunities...');
    this.viewOpportunitiesOpen = false;
  }

  //#endregion Opportunities

  //#region Private functions

  private handleSaveAndClose(args: any): void {
    this.logger.debug('handleSaveAndClose...', args);
    const { action, data, mode } = args;
    if (data && data.linkType === 'URL' && data.document_file_name) {
      data.document_file_name = null;
      this.dataService.deleteFile(data.id, "resource")
        .then(() => {
          this.logger.debug('deleteFile:done...');
          this.handleSave(data, mode);
        })
        .catch(err => this.logger.error('handleSaveAndClose:error: ', err))
    } else {
      this.handleSave(data, mode);
    }
    // this.dataService.addEdit(data, mode)
    //   .then(resp => {
    //     this.notify(mode === FormModes.NEW ? 'Resource Added' : 'Resource Updated')
    //     this.dataService.query();
    //   })
    //   .catch(err => {
    //     this.logger.error('handleSaveAndClose:error: ', err);
    //     this.notify("Error Adding/Updating Resource", true);
    //   })
    //   .finally(() => this.editWinOpen = false);
    // this.editWinOpen = false;
  }

  private handleSave(data: Resource, mode: FormModes): void {
    this.dataService.addEdit(data, mode)
      .then(resp => {
        this.notify(mode === FormModes.NEW ? 'Resource Added' : 'Resource Updated')
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('handleSaveAndClose:error: ', err);
        this.notify("Error Adding/Updating Resource", true);
      })
      .finally(() => this.editWinOpen = false);
  }

  private handleRemove(args: Resource): void {
    if (args && !args.id) {
      this.logger.error('handlRemove: No id given');
    }
    args.delete();

    this.dataService.update(args)
      .then(resp => {
        this.logger.debug('handleRemove:resp: ', resp);
        this.notify("Resource Deleted");
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('handleRemove:err: ', err);
        this.notify("Error Deleting Resource", true);
        this.confirmDeleteItem = true;
      });
  }

  private loadData(): void {
    // this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    this.notificationService.show({
      appendTo: this.appendTo,
      content: msg,
      animation: { type: "fade", duration: 300 },
      type: { style: !error ? "success" : "error", icon: true },
      position: {
        horizontal: "right",
        vertical: "top"
      },
      height: 50,
      // width: 150,
      hideAfter: 2000
    });
  }

  //#endregion


}
